import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { POS_OFFLINE_FLAGS, type PosOfflineFlag } from '@mlm/shared';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, cn } from '@mlm/ui';
import {
  listOfflineSales,
  retryOfflineSale,
  reviewOfflineSale,
  type OfflineSaleRow,
} from '../lib/api';
import { hasPerm } from '../lib/auth-store';

const peso = (v: string) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const flagLabel = (flag: string) => POS_OFFLINE_FLAGS[flag as PosOfflineFlag] ?? flag;

function StatusBadge({ row }: { row: OfflineSaleRow }) {
  if (row.status === 'failed') return <Badge variant="destructive">Not applied</Badge>;
  if (row.status === 'applied') {
    return row.flags.length > 0 ? (
      <Badge variant="outline">Applied, check it</Badge>
    ) : (
      <Badge variant="secondary">Applied</Badge>
    );
  }
  return <Badge variant="outline">In progress</Badge>;
}

/**
 * Sales that came in from tills with no internet. Every one is stored as soon as
 * it arrives, so nothing here is lost: this page is for the few that could not be
 * applied (retry them once the cause is fixed) or that went through but deserve
 * a look, such as stock going below zero.
 */
export function OfflineSalesPage() {
  const [view, setView] = useState<'attention' | 'all'>('attention');
  const qc = useQueryClient();
  const canOpenOrders = hasPerm('order.read');
  const salesQ = useQuery({
    queryKey: ['offline-sales', view],
    queryFn: () => listOfflineSales(view),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['offline-sales'] });
  const retry = useMutation({
    mutationFn: retryOfflineSale,
    onSuccess: refresh,
  });
  const review = useMutation({
    mutationFn: reviewOfflineSale,
    meta: { successMessage: 'Marked as reviewed.' },
    onSuccess: refresh,
  });
  const rows = salesQ.data ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle>Offline sales</CardTitle>
        <div className="flex gap-2">
          {(
            [
              ['attention', 'Needs attention'],
              ['all', 'All'],
            ] as const
          ).map(([value, label]) => (
            <Button
              key={value}
              size="sm"
              variant={view === value ? 'default' : 'outline'}
              onClick={() => setView(value)}
            >
              {label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {salesQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : salesQ.isError ? (
          <p className="text-sm text-destructive">Could not load offline sales.</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {view === 'attention'
              ? 'Nothing needs attention. Sales from offline tills that have a problem appear here.'
              : 'No sales have come in from offline tills yet.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-2 py-2">Receipt no.</th>
                  <th className="px-2 py-2">Order</th>
                  <th className="px-2 py-2">Sold</th>
                  <th className="px-2 py-2">Branch</th>
                  <th className="px-2 py-2">Cashier</th>
                  <th className="px-2 py-2 text-right">Total</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b align-top last:border-0">
                    <td className="px-2 py-2 font-mono text-xs">{row.localNumber}</td>
                    <td className="px-2 py-2 font-mono text-xs">
                      {row.orderId && canOpenOrders ? (
                        <Link className="underline" to={`/orders?q=${row.orderCode ?? ''}`}>
                          {row.orderCode}
                        </Link>
                      ) : (
                        (row.orderCode ?? '—')
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs whitespace-nowrap">
                      {new Date(row.soldAt).toLocaleString()}
                    </td>
                    <td className="px-2 py-2 text-xs">{row.branchName ?? '—'}</td>
                    <td className="px-2 py-2 text-xs">{row.soldByName ?? '—'}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{peso(row.total)}</td>
                    <td className="px-2 py-2">
                      <StatusBadge row={row} />
                      {(row.flags.length > 0 || row.error) && (
                        <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                          {row.flags.map((flag) => (
                            <li key={flag}>{flagLabel(flag)}</li>
                          ))}
                          {row.error && <li className="text-destructive">{row.error}</li>}
                        </ul>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('flex justify-end gap-2')}>
                        {row.status !== 'applied' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={retry.isPending}
                            onClick={() => retry.mutate(row.id)}
                          >
                            Retry
                          </Button>
                        )}
                        {!row.reviewedAt && (row.status === 'failed' || row.flags.length > 0) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={review.isPending}
                            onClick={() => review.mutate(row.id)}
                          >
                            Mark reviewed
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
